<template>
  <div v-if="visible" class="sp-quantum-select-remote-status">
    <sp-progress v-if="pending" type="circular" size="small" :aria-label="text"></sp-progress>
    <slot v-if="isEmpty" name="no-data">{{ text }}</slot>
    <span v-else>{{ footerText }}</span>
    <sp-button v-if="hasError" type="button" data-selection-control @click="handleRetry">{{ retryLabel }}</sp-button>
    <sp-button v-else-if="canLoadMore" type="button" data-selection-control @click="handleLoadMore">{{
      loadMoreLabel
    }}</sp-button>
  </div>
</template>

<script setup>
import { computed, inject, ref } from "vue";
import {
  quantumSelectRemoteKey,
  RemoteStatus,
} from "#ce/components/SpQuantumSelect/composables/useQuantumSelectRemote";
import { i18n, remoteStatusText, whenI18nReady } from "#ce/components/SpQuantumSelect/i18n";

const PENDING_STATUSES = [RemoteStatus.DEBOUNCING, RemoteStatus.LOADING, RemoteStatus.LOADING_MORE];

const remote = inject(quantumSelectRemoteKey);
const { state } = remote;
const ready = ref(false);

const status = computed(() => state.value.status);
const visible = computed(() => ready.value && remote.enabled.value);
const text = computed(() => remoteStatusText(state.value, remote.minimum.value, remote.count.value));
const pending = computed(() => PENDING_STATUSES.includes(status.value));
const isEmpty = computed(() => status.value === RemoteStatus.EMPTY);
const hasError = computed(() => status.value === RemoteStatus.ERROR);
const isReady = computed(() => status.value === RemoteStatus.READY);
const canLoadMore = computed(() => isReady.value && state.value.hasMore);
const footerText = computed(() => (isReady.value && !state.value.hasMore ? i18n.global.t("end") : text.value));
const retryLabel = computed(() => i18n.global.t("retry"));
const loadMoreLabel = computed(() => i18n.global.t("load_more"));

function markReady() {
  ready.value = true;
}

function handleRetry() {
  remote.retryRemote();
}

function handleLoadMore() {
  remote.loadMore();
}

whenI18nReady.then(markReady);
</script>

<style scoped lang="scss">
.sp-quantum-select-remote-status {
  display: flex;
  align-items: center;
  gap: var(--sp-ref-scale-8);
  padding: var(--sp-ref-scale-12);
  color: var(--sp-sys-color-on-surface);
}
</style>
